import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Popover,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { t } from "i18next";
import { FilterTag } from "./FilterTag";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../shared/views/CustomPopover";
import { AssessmentType } from "../../../../map/models/MapFilters";

type NewValue = Record<AssessmentType, boolean>;

const AssessmentTypeFiltersBase: React.FC = () => {
  const { filterStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [newValue, setNewValue] = useState<NewValue>({
    Remote: false,
    OnSite: false,
  });

  const parsedNewValue = parseNewValue(newValue);

  useEffect(() => {
    if (open) {
      setNewValue(() => ({
        Remote: filterStore.assessmentTypeFilters === "Remote",
        OnSite: filterStore.assessmentTypeFilters === "OnSite",
      }));
    }
    //eslint-disable-next-line
  }, [open]);

  const handleClear = () => {
    setNewValue({
      Remote: false,
      OnSite: false,
    });
  };

  const handleSubmit = () => {
    filterStore.setAssessmentTypeFilters(parsedNewValue);
    onClose();
  };

  const removeActiveFilter = () => {
    filterStore.setAssessmentTypeFilters(null);
  };

  return (
    <Stack>
      <CustomPopover
        open={open}
        onClose={onClose}
        trigger={
          <Button
            fontSize={"sm"}
            size="sm"
            variant="outline"
            colorPalette="primary"
            h="unset"
            py={1.5}
            onClick={onToggle}
          >
            Assessment Type
          </Button>
        }
      >
        <Popover.Header fontWeight={"semibold"}>
          Filter by assessment Type
        </Popover.Header>
        <Popover.Body maxH="360px" overflowY="auto">
          <VStack alignItems="start">
            {Object.values(AssessmentType).map((type) => (
              <Checkbox.Root
                key={type}
                colorPalette="primary"
                name={type}
                checked={newValue[type]}
                onCheckedChange={({ checked }) => {
                  setNewValue((prev) => ({
                    ...prev,
                    [type]: checked,
                  }));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>
                  <Text fontSize="sm" lineClamp={1}>
                    {t(`map:assessmentTypes.${type}`)}
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
            ))}
          </VStack>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette="primary" size="sm">
            <Button onClick={handleClear} disabled={!parsedNewValue}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.assessmentTypeFilters && (
        <Box m={-1}>
          <FilterTag onClose={removeActiveFilter}>
            {t(`map:assessmentTypes.${filterStore.assessmentTypeFilters}`)}
          </FilterTag>
        </Box>
      )}
    </Stack>
  );
};

export const AssessmentTypeFilters = observer(AssessmentTypeFiltersBase);

const parseNewValue = (newValue: NewValue): AssessmentType | null => {
  if (newValue.Remote === newValue.OnSite) return null;
  if (newValue.Remote) {
    return AssessmentType.Remote;
  }
  return AssessmentType.OnSite;
};
